import { useEffect, useRef, useState } from 'react'

import { deleteViewPreset, getViewPresets, putViewPreset } from '@/lib/api/client/views'
import { readLegacyWarehouseColumns } from '@/components/views/ColumnOrderChooser'
import type { InventoryColumnId } from '@/lib/views/columns/inventory'
import { INVENTORY_COLUMN_REGISTRY } from '@/lib/views/columns/inventory'
import { isViewPresetKey, type ViewPresetKey, type ViewPresetOverride } from '@/lib/views/presets'

type InventoryOverride = ViewPresetOverride<InventoryColumnId>

type SavedOverrides = Partial<Record<ViewPresetKey, InventoryOverride>>

function isInventoryColumn(value: string): value is InventoryColumnId {
  return INVENTORY_COLUMN_REGISTRY.some((column) => column.id === value)
}

function isInventoryOverride(value: InventoryOverride): boolean {
  return (value.columns ?? []).every(isInventoryColumn)
    && (value.sort === undefined || isInventoryColumn(value.sort.column))
}

const LEGACY_PLATFORM_COLUMNS: ReadonlySet<InventoryColumnId> = new Set([
  'marketplaces', 'wildberriesPrice', 'ymarketPrice', 'ozonPrice', 'oriSitePrice', 'readiness',
])

function normalizePlatformsOverride(override: InventoryOverride): InventoryOverride {
  const columns = override.columns ?? []
  const hasCompactColumn = columns.includes('storefronts')
  const hasLegacyPriceLayout = columns.some((column) => LEGACY_PLATFORM_COLUMNS.has(column))
  if (hasCompactColumn || !hasLegacyPriceLayout) return override
  const firstLegacyIndex = columns.findIndex((column) => LEGACY_PLATFORM_COLUMNS.has(column))
  const next = columns.filter((column) => !LEGACY_PLATFORM_COLUMNS.has(column))
  next.splice(firstLegacyIndex, 0, 'storefronts')
  return { ...override, columns: next }
}

function overrideFromResponse<Column extends string>(
  value: { columns: string[]; sort: { column: string; direction: 'ascending' | 'descending' } },
  isColumn: (value: string) => value is Column,
): ViewPresetOverride<Column> | null {
  const columns = value.columns.filter(isColumn)
  if (columns.length !== value.columns.length || !isColumn(value.sort.column)) return null
  return {
    columns,
    sort: { column: value.sort.column, direction: value.sort.direction },
  }
}

export function useViewPresets() {
  const [overrides, setOverrides] = useState<SavedOverrides>({})
  const [saving, setSaving] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const legacy = useRef(readLegacyWarehouseColumns())

  useEffect(() => {
    let active = true
    getViewPresets()
      .then((values) => {
        if (!active) return
        const next: SavedOverrides = {}
        for (const value of values) {
          if (isViewPresetKey(value.key)) {
            const override = overrideFromResponse(value, isInventoryColumn)
            if (override) next[value.key] = value.key === 'platforms' ? normalizePlatformsOverride(override) : override
          }
        }
        setOverrides(next)
        setLoaded(true)
        const seeded = legacy.current
        legacy.current = null
        if (seeded) {
          setSaving(true)
          void putViewPreset('actual', {
            columns: seeded,
            sort: { column: 'activity', direction: 'descending' },
          }).then((response) => {
            if (!active) return
            const override = overrideFromResponse(response, isInventoryColumn)
            if (override) setOverrides((current) => ({ ...current, actual: override }))
          }).catch(() => undefined).finally(() => { if (active) setSaving(false) })
        }
      })
      .catch(() => { if (active) setLoaded(true) })
    return () => { active = false }
  }, [])

  async function save(key: ViewPresetKey, override: InventoryOverride): Promise<void> {
    setSaving(true)
    if (isInventoryOverride(override)) {
      setOverrides((current) => ({ ...current, [key]: override }))
    }
    try {
      const response = await putViewPreset(key, {
        columns: override.columns ?? [],
        sort: override.sort ?? { column: 'article', direction: 'ascending' },
      })
      const saved = overrideFromResponse(response, isInventoryColumn)
      if (saved) setOverrides((current) => ({ ...current, [key]: saved }))
    } catch {
      // The local optimistic state keeps the table usable until the next read.
    } finally {
      setSaving(false)
    }
  }

  const reset = async (key: ViewPresetKey): Promise<void> => {
    setSaving(true)
    setOverrides((current) => {
      const next = { ...current }
      delete next[key]
      return next
    })
    try { await deleteViewPreset(key) } catch { /* the built-in remains usable */ } finally { setSaving(false) }
  }

  return { overrides, saving, loaded, save, reset }
}
